
import { useState, useEffect, useCallback } from "react"
import ImageUploadForm from "./graderComponents/ImageUploadForm"
import QuestionsTable from "./graderComponents/QuestionsTable"
import GradedHistory from "./graderComponents/GradedHistory"
import { addToHistory, getAllGroups, addGroup } from "./graderComponents/indexedDBService"
import { Helmet } from "react-helmet"


function ExamGrader() {
  // Questions and UI state
  const [questions, setQuestions] = useState([])
  const [gradedQuestions, setGradedQuestions] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [isGrading, setIsGrading] = useState(null)
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [refreshGroupHistory, setRefreshGroupHistory] = useState(null)
  const [selectGroupCallback, setSelectGroupCallback] = useState(null)
  const [allGroups, setAllGroups] = useState([])
  const [forceRefresh, setForceRefresh] = useState(0)

  //Const for meta
  const pageUrl = 'https://www.aiatedu.com/exam-grader'
  const pageTitle = 'Exam Grader | AIAT'
  const pageDesc =
    'Upload and grade exam questions with AIAT’s AI-powered Exam Grader. Organize your history, score questions, and review detailed feedback.'

  // Load all groups - this function will be called whenever we need to refresh the groups list
  const loadGroups = useCallback(async () => {
    try {
      const groups = await getAllGroups()
      setAllGroups(groups)
      return groups
    } catch (error) {
      console.error("Failed to load groups:", error)
      return []
    }
  }, [])

  // Load groups on component mount and when forceRefresh changes
  useEffect(() => {
    loadGroups()
  }, [loadGroups, forceRefresh])

  // Process a single image
  const processImage = async (imageData, markingImage) => {
    try {
      const requestBody = {
        questionImage: imageData,
        markingImage: markingImage || null,
      }

      const response = await fetch("/api/upload-solution", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })

      const data = await response.json()
      return data.questions || []
    } catch (error) {
      console.error("Upload error for image:", error)
      return []
    }
  }

  // Helper function to merge questions with empty question content
  const mergeQuestions = (questionsArray) => {
    if (!questionsArray || questionsArray.length === 0) return []

    const mergedQuestions = []
    let lastNonEmptyQuestionIndex = -1

    for (let i = 0; i < questionsArray.length; i++) {
      const currentQuestion = questionsArray[i]

      // Check if current question has empty question content but non-empty solution
      const hasEmptyQuestion = !currentQuestion.questionContent || currentQuestion.questionContent.trim() === ""
      const hasNonEmptySolution = currentQuestion.studentSolution && currentQuestion.studentSolution.trim() !== ""

      if (hasEmptyQuestion && hasNonEmptySolution && lastNonEmptyQuestionIndex !== -1) {
        // Merge this solution with the last non-empty question
        const lastQuestion = mergedQuestions[lastNonEmptyQuestionIndex]

        // Append the current solution to the last question's solution
        lastQuestion.studentSolution =
          (lastQuestion.studentSolution || "") + "\n" + (currentQuestion.studentSolution || "")

        // If there's a marking scheme, merge that too
        if (currentQuestion.markingScheme) {
          lastQuestion.markingScheme = (lastQuestion.markingScheme || "") + "\n" + (currentQuestion.markingScheme || "")
        }
      } else {
        // Add as a new question
        mergedQuestions.push({ ...currentQuestion })

        // If this question has non-empty content, update the lastNonEmptyQuestionIndex
        if (!hasEmptyQuestion) {
          lastNonEmptyQuestionIndex = mergedQuestions.length - 1
        }
      }
    }

    // Filter out any questions that still have empty question content
    return mergedQuestions.filter((q) => q.questionContent && q.questionContent.trim() !== "")
  }

  // Submit the Base64 images as a JSON payload, one by one
  const handleUpload = async (imagesToUpload, markingImage) => {
    if (imagesToUpload.length === 0) {
      alert("Please upload or paste at least one question image.")
      return
    }

    setIsUploading(true)
    setCurrentUploadIndex(0)
    setUploadProgress(0)

    let allNewQuestions = []

    // Process images sequentially
    for (let i = 0; i < imagesToUpload.length; i++) {
      setCurrentUploadIndex(i)
      setUploadProgress(Math.round((i / imagesToUpload.length) * 100))

      const newQuestions = await processImage(imagesToUpload[i], markingImage)
      allNewQuestions = [...allNewQuestions, ...newQuestions]

      // Update progress
      setUploadProgress(Math.round(((i + 1) / imagesToUpload.length) * 100))
    }

    // Merge questions with empty question content but non-empty solutions
    const mergedQuestions = mergeQuestions(allNewQuestions)

    // Add merged questions to existing ones
    setQuestions((prev) => {
      const updatedQuestions = [...prev, ...mergedQuestions]
      return updatedQuestions
    })

    setIsUploading(false)
    setUploadProgress(100)

    return true // Return success
  }

  // Remove a question from the table
  const handleRemoveQuestion = (index) => {
    setQuestions((prev) => {
      const updated = [...prev]
      updated.splice(index, 1)
      return updated
    })
  }

  // Get or create a folder for today's date
  const getOrCreateTodayFolder = async () => {
    // Format today's date as "Month Day, Year" (e.g., "May 5, 2025")
    // This ensures we only use the date part, not the time
    const today = new Date()
    const options = { year: "numeric", month: "long", day: "numeric" }
    const folderName = today.toLocaleDateString("en-US", options)


    // Reload groups to ensure we have the latest list
    const currentGroups = await loadGroups()

    // Check if a folder with today's date already exists
    const existingFolder = currentGroups.find((group) => group.name === folderName)

    if (existingFolder) {
      return existingFolder.id
    } else {
      // Create a new folder with today's date
      try {
        const newFolderId = await addGroup(folderName)

        // Force a refresh of the groups list
        setForceRefresh((prev) => prev + 1)

        return newFolderId
      } catch (error) {
        console.error("Failed to create folder for today:", error)
        return null
      }
    }
  }

  // Add a graded question to a group in IndexedDB
  const handleAddToGroup = async (question, groupId) => {
    if (!groupId) {
      alert("Please select a group first")
      return false
    }

    try {
      await addToHistory(question, groupId)

      // Signal the GradedHistory component to refresh the current group's content
      if (refreshGroupHistory) {
        refreshGroupHistory(groupId)
      }

      return true
    } catch (error) {
      console.error("Failed to add question to group:", error)
      alert("Failed to add question to group")
      return false
    }
  }

  // Trigger grading for a specific question
  const handleGrade = async (question, index) => {
    setIsGrading(index)

    try {
      const requestBody = {
        questionContent: question.questionContent,
        studentSolution: question.studentSolution,
        markingScheme: question.markingScheme,
      }

      const responseData = await fetch("/api/grade", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })
      const response = await responseData.json()

      // Handle the new response format
      const gradedQuestion = {
        ...question,
        grade: response.grade || "0",
        maxgrade: response.maxgrade || "0",
        feedback: response.feedback || "",
        questionNumber: index + 1,
      }

      // Safely update the questions array
      setQuestions((prevQuestions) => {
        const updated = [...prevQuestions]
        updated[index] = gradedQuestion
        return updated
      })

      // Check if this question is already in gradedQuestions to avoid duplicates
      setGradedQuestions((prevGraded) => {
        const existingIndex = prevGraded.findIndex(
          (q) => q.questionContent === question.questionContent && q.studentSolution === question.studentSolution,
        )

        if (existingIndex >= 0) {
          // Update existing graded question
          const updatedGraded = [...prevGraded]
          updatedGraded[existingIndex] = gradedQuestion
          return updatedGraded
        } else {
          // Add new graded question
          return [...prevGraded, gradedQuestion]
        }
      })

      // Get or create today's folder and add the question to it
      try {
        const todayFolderId = await getOrCreateTodayFolder()

        if (todayFolderId) {
          // Add the question to the folder
          const success = await handleAddToGroup(gradedQuestion, todayFolderId)

          // Force refresh the groups list to show the new folder
          setForceRefresh((prev) => prev + 1)

          // Select the today's folder in the sidebar
          if (selectGroupCallback) {
            selectGroupCallback(todayFolderId)
          } else {
          }
        } else {
        }
      } catch (error) {
        console.error("Error in folder creation/selection:", error)
      }

      return gradedQuestion
    } catch (error) {
      console.error("Grading error: ", error)
      return null
    } finally {
      setIsGrading(null)
    }
  }

  // Grade all ungraded questions sequentially
  const handleGradeAll = async (ungradedQuestions) => {
    if (ungradedQuestions.length === 0 || isGrading !== null) return []

    // Find the indices of ungraded questions in the main questions array
    const ungradedIndices = ungradedQuestions
      .map((uq) =>
        questions.findIndex(
          (q) => q.questionContent === uq.questionContent && q.studentSolution === uq.studentSolution,
        ),
      )
      .filter((idx) => idx !== -1)
    const gradedResults = []
    // Grade each question sequentially
    for (let i = 0; i < ungradedIndices.length; i++) {
      const index = ungradedIndices[i]
      const question = questions[index]

      // Wait for the current question to be graded before moving to the next
      const gradedQuestion = await handleGrade(question, index)
      if (gradedQuestion) {
        gradedResults.push(gradedQuestion)
      }

      // Small delay between questions to avoid overwhelming the API
      if (i < ungradedIndices.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500))
      }
    }
    return gradedResults
  }

  return (
    <>
      <Helmet>
        {/* 1. CMP */}
      <script
        id="usercentrics-cmp"
        src="https://app.usercentrics.eu/browser-ui/latest/loader.js"
        data-settings-id="R7Xi1xeFRKhrKk"
        async
      />
      {/* 2. gtag */}
      <script
        async
        src="https://www.googletagmanager.com/gtag/js?id=AW-17303305286"
      />
      <script>{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'AW-17303305286');
      `}</script>
      {/* 3a. GTM head */}
      <script>{`
        (function(w,d,s,l,i){
          w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
          var f=d.getElementsByTagName(s)[0], j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
          j.async=true; j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
          f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-MBKBFC9M');
      `}</script>
        {/* Browser tab title */}
        <title>{pageTitle}</title>

        {/* Basic SEO */}
        <meta name="description" content={pageDesc} />
        <meta
          name="keywords"
          content="exam grader, AI grading, question scoring, GradeGenius, AIAT"
        />

        {/* Favicon */}
        <link rel="icon" href="/favicon.ico" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={pageUrl} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
      </Helmet>
      <div className="flex flex-col md:flex-row h-screen bg-gradient-to-br from-blue-50 via-gray-50 to-white overflow-hidden">
        {/* Main content */}
        <main className="flex-1 p-4 md:p-6 overflow-y-auto relative">
          {/* Background pattern using Tailwind */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iMSIgZmlsbD0icmdiYSg1OSwgMTMwLCAyNDYsIDAuMSkiLz48L3N2Zz4=')] bg-blue-50/30 pointer-events-none z-0"></div>

        <div className="relative z-10">
          {/* Modern title with enhanced styling */}
          <div className="mb-8 md:mb-10 animate-fadeIn" style={{ animationDelay: "0.1s" }}>
            <div className="inline-block relative">
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 pb-1">
                GradeGenius
              </h1>
              <div className="absolute -bottom-1 left-0 w-1/2 h-1 bg-gradient-to-r from-blue-600 to-transparent rounded-full"></div>
            </div>
            <p className="text-gray-500 mt-3 text-lg">Intelligent exam grading & organization system</p>
          </div>

          <ImageUploadForm
            onUpload={handleUpload}
            isUploading={isUploading}
            currentUploadIndex={currentUploadIndex}
            uploadProgress={uploadProgress}
          />

          {questions.length > 0 && (
            <QuestionsTable
              questions={questions}
              isUploading={isUploading}
              isGrading={isGrading}
              onGrade={handleGrade}
              onRemove={handleRemoveQuestion}
              onGradeAll={handleGradeAll}
            />
          )}
        </div>
      </main>

        {/* Sidebar for graded questions */}
        <GradedHistory
          gradedQuestions={gradedQuestions}
          onAddToGroup={handleAddToGroup}
          setRefreshCallback={setRefreshGroupHistory}
          setSelectGroupCallback={setSelectGroupCallback}
          forceRefresh={forceRefresh}
        />
      </div>
    </>
  )
}

export default ExamGrader
